import { msg } from "@omegagrid/localize";
import rp, {
	SqlEditorAdapter, SqlEditorOptions, SqlExecOptions, SqlCompileResult, SqlExecCallbackArgs, SqlParseResult,
	CodeLinkProvider, createSqlIncludeLinkProvider, SqlIncludeMacro } from "@reporting/rp-app-host";
import { HistoryItem } from '@reporting/rp-types/core';
import { Query } from "@reporting/rp-types/ext";

export class QuerySqlEditorAdapter extends SqlEditorAdapter {

	constructor(
		protected query: Query,
		protected getParameterValues: () => Record<string, unknown> = null
	) { super() }

	async getContent() : Promise<string> {
		if (this.query.id_thread == 0) return null;
		return await rp.ms('extensions').get<string>(`queryContent/${this.query.id_thread}`);
	}

	async getHistoryContent(idz: number): Promise<string> {
		return rp.ms('extensions').get<string>(`queryHistoryContent/${idz}`);
	}

	/** Ctrl+click on the query name of a `{{include}}` / `{{include_once}}` macro opens that query. */
	getLinkProvider(): CodeLinkProvider {
		return createSqlIncludeLinkProvider({
			tooltip: msg('Open'),
			open: macro => this.openIncludedQuery(macro),
		});
	}

	/**
	 * Looks the included query up the way p_CUSTOM_QUERY does: by DESCRIPTION among the queries of the same
	 * project (custom page), preferring the accounting the macro names (or the current one) over the others.
	 */
	private async openIncludedQuery(macro: SqlIncludeMacro) {
		const accounting = macro.accounting ?? rp.accountingProvider.a;
		const queries = this.query.id_custom_page ? await rp.ms('extensions').get<Query[]>('queries', {
			fields: ['id_thread', 'description', 'accounting'],
			where: { id_custom_page: this.query.id_custom_page, description: macro.name },
		}) : [];
		const query = queries?.find(q => q.accounting === accounting) ?? queries?.[0];
		if (!query) {
			rp.host.showAlert({
				type: 'warning',
				title: 'SQL Editor',
				content: msg("Source '{name}' was not found.", { data: { name: macro.name } }),
			});
			return;
		}
		await rp.host.open(`extensions-query-${query.id_thread}`, { title: query.description });
	}

	async parse(sql: string) : Promise<SqlParseResult> {
		return await rp.ms('extensions').post<SqlParseResult>(`parse/${this.query.id_thread}`, sql);
	}

	async exec(sql: string, options?: SqlExecOptions, cb?: (args: SqlExecCallbackArgs) => void) : Promise<void> {
		await this.execStream(rp.ms('extensions'), `exec/${this.query.id_thread}`, sql, options, cb, {
			customParams: this.getParameterValues ? this.getParameterValues() : {}
		});
	}

	async compile(sql: string) : Promise<SqlCompileResult> {
		const client = rp.ms('extensions');
		const response = await client.postRaw(`compile/${this.query.id_thread}`, sql);
		return {
			sql: await client.handleResponse<string>(response, false),
			offset: parseInt(response.headers.get('X-Error-Offset')) || 0
		};
	}

	async save(sql: string) {
		await rp.ms('extensions').post(`queryContent/${this.query.id_thread}`, sql);
	}

	async abort() {
		await this.abortSpid(rp.ms('extensions'));
	}

	getHistoryItems(): Promise<HistoryItem[]> {
		return rp.ms('extensions').get<HistoryItem[]>(`queryHistory/${this.query.id_thread}`);
	}

	getCurrentItem(): HistoryItem {
		return this.query;
	}

	getOptions(): SqlEditorOptions {
		return this.createOptions({history: true});
	}

}
